import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import { Box, IconButton, useMediaQuery } from '@mui/material';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components';
import { ComplianceTypes, Routes } from '@/constants';
import { AnalyticsService, EVENTS } from '@/services';
import {
  playerSessionsSelectors,
  selectActiveEpisodeId,
  selectPlayerRules,
  selectSessionUserId,
  selectUserProfileResult,
  useAppSelector,
  useFetchEpisodeAssessmentQuery,
  useFetchEpisodeQuery,
  useSubmitUserAssessmentMutation,
} from '@/store';
import {
  createRouteWithParams,
  isTestModeEnabled,
  SnackbarUtils,
} from '@/utils';

import {
  AssessmentHeader,
  AssessmentSkeleton,
  ConfirmModal,
  OverlayMessage,
  QuestionBanner,
  QuestionContent,
  QuestionNavigation,
} from './components';

export const EpisodeAssessment = () => {
  const navigate = useNavigate();
  const { episodeId } = useParams();
  const isTestMode = isTestModeEnabled();
  const isMdDown = useMediaQuery(theme => theme.breakpoints.down('md'));

  const playerRules = useAppSelector(selectPlayerRules);

  const activeEpisodeId = useAppSelector(selectActiveEpisodeId);
  const userId = useAppSelector(selectSessionUserId);
  const { data: profile } = useAppSelector(selectUserProfileResult);
  const playerSession = useAppSelector(state =>
    playerSessionsSelectors.selectById(state, episodeId!),
  );

  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const { data: assessment, isLoading: isAssessmentLoading } =
    useFetchEpisodeAssessmentQuery(
      { episodeId: episodeId!, testMode: isTestMode },
      { skip: !episodeId },
    );

  const {
    data: episode,
    isLoading: isEpisodeLoading,
    isUninitialized,
  } = useFetchEpisodeQuery(
    {
      episodeId: episodeId!,
      compliance: ComplianceTypes.CPE,
      includeArchived: true,
    },
    { skip: !episodeId },
  );

  const [submitUserAssessment, { data: submitUserAssessmentResponse, reset }] =
    useSubmitUserAssessmentMutation();

  // TODO: The listening rule is inferred from seek rule, for now
  const requiresListening = playerRules?.cpe?.includes('no_seek_forward');
  const requiresQuizzes = playerRules?.cpe?.includes(
    'assessment_requires_completed_quizzes',
  );

  // Lock assessment if chapters not completed, or if there are quizzes to take
  const hasListened = playerSession?.status === 'assessmentReady';
  const hasCompletedQuizzes =
    hasListened && playerSession?.availableQuizCount === 0;

  const isCompleted = playerSession?.extraStates?.creditState === 'earned';
  const isAssessmentLocked =
    !isTestMode &&
    ((requiresListening && !hasListened) ||
      (requiresQuizzes && !hasCompletedQuizzes));
  const isPreview = isAssessmentLocked && !isCompleted;
  const hasSkippedQuestion =
    !isCompleted &&
    answers.includes(null) &&
    !!assessment &&
    answers[assessment.questions.length - 1] !== null;
  const storageKey = useMemo(
    () => `assessmentAnswers_${userId}_${episodeId}`,
    [episodeId, userId],
  );

  const navigateToEpisode = useCallback(() => {
    const episodeUrl = createRouteWithParams(Routes.EPISODE, {
      episodeId,
    });
    navigate(episodeUrl);
  }, [episodeId, navigate]);

  const updateSelectedQuestion = (index: number, component?: string) => {
    if (!episode || !assessment) return;

    AnalyticsService.logEvent(EVENTS.ASSESSMENT_EVENTS.QUESTION_NAVIGATE, {
      episode_id: episode.episodeId,
      assessment_id: assessment.userAssessmentId,
      from_index: currentQuestionIndex,
      to_index: index,
      total_questions: assessment.questions.length,
      location: {
        component,
      },
    });

    setCurrentQuestionIndex(
      Math.max(0, Math.min(index, (assessment?.questions.length ?? 0) - 1)),
    );
  };

  const resetAssessment = () => {
    reset();
    updateStoredAnswers(assessment?.questions.map(() => null) ?? []);
    setCurrentQuestionIndex(0);
  };

  const updateStoredAnswers = (newAnswers: (number | null)[]) => {
    setAnswers(newAnswers);
    localStorage.setItem(storageKey, JSON.stringify(newAnswers));
  };

  const onSubmitAssessment = async () => {
    if (!episodeId || !assessment || !playerSession || answers.includes(null)) {
      return;
    }

    try {
      const result = await submitUserAssessment({
        episodeId,
        // @ts-expect-error: button is disabled if there are any nulls in answers
        answers,
        sessionId: playerSession.sessionId,
      }).unwrap();

      AnalyticsService.assessmentAttempt({
        episodeId,
        assessmentId: assessment.userAssessmentId,
        answer: answers,
        score: result.score,
        pass: result.isPass,
      });

      if (result.isPass) {
        AnalyticsService.episodeComplete({
          episodeId,
        });
      }
    } catch {
      SnackbarUtils.error(
        'Failed to submit assessment. Please try again or contact support.',
      );
    }
  };

  const logAssessmentLockedEvent = useEffectEvent(() => {
    AnalyticsService.logEvent(EVENTS.ASSESSMENT_EVENTS.LOCKED, {
      episode_id: episode!.episodeId,
      assessment_id: assessment!.userAssessmentId,
      total_questions: assessment!.questions.length,
      reason:
        requiresListening && playerSession?.status !== 'assessmentReady'
          ? 'insufficient_listen'
          : 'chapter_quizzes_incomplete',
    });
  });

  useEffect(() => {
    // Non-cpe sessions should never see assessment page
    if (playerSession && playerSession?.fsmType !== 'cpe_fsm') {
      navigateToEpisode();
    }
  }, [playerSession, navigateToEpisode]);

  useEffect(() => {
    if (assessment && assessment.questions.length > 0) {
      const defaultAnswers = assessment.questions.map(() => null);
      try {
        const parsedAnswers = JSON.parse(localStorage.getItem(storageKey)!);
        setAnswers(parsedAnswers || defaultAnswers);
      } catch {
        setAnswers(defaultAnswers);
      }
    }
  }, [assessment, storageKey]);

  useEffect(() => {
    if (activeEpisodeId) {
      window.Intercom('update', {
        vertical_padding: 200,
      });
    }
  }, [userId, activeEpisodeId, profile]);

  useEffect(() => {
    if (isPreview && episode && assessment) {
      logAssessmentLockedEvent();
    }
  }, [isPreview, episode, assessment]);

  if (
    isEpisodeLoading ||
    isAssessmentLoading ||
    isUninitialized ||
    !assessment ||
    !playerSession ||
    !episode
  ) {
    return <AssessmentSkeleton />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: ({ dimensions }) =>
          activeEpisodeId ? dimensions.miniPlayer.height : 0,
      }}>
      <OverlayMessage
        score={submitUserAssessmentResponse?.score}
        episode={episode}
        assessment={assessment}
        resetAssessment={resetAssessment}
        navigateToEpisode={navigateToEpisode}
      />
      {isMdDown && (
        <AssessmentHeader
          episode={episode}
          assessment={assessment}
          quitAssessment={navigateToEpisode}
          isPreview={isPreview}
        />
      )}
      <QuestionNavigation
        assessment={assessment}
        currentQuestionIndex={currentQuestionIndex}
        onQuestionSelect={index =>
          updateSelectedQuestion(index, 'sidebar_navigation')
        }
        answers={answers}
        isCompleted={isCompleted}
        isPreview={isPreview}
      />
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
        }}>
        {!isMdDown && (
          <AssessmentHeader
            episode={episode}
            assessment={assessment}
            quitAssessment={navigateToEpisode}
            isPreview={isPreview}
          />
        )}
        {!isCompleted && (
          <QuestionBanner
            assessment={assessment}
            isPreview={isPreview}
            hasCompletedQuizzes={hasCompletedQuizzes}
            hasSkippedQuestion={hasSkippedQuestion}
          />
        )}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', position: 'relative' }}>
          <QuestionContent
            assessment={assessment}
            answers={answers}
            currentQuestionIndex={currentQuestionIndex}
            isLocked={isCompleted || isAssessmentLocked}
            setAnswer={({ questionIndex, value }) => {
              updateStoredAnswers(answers.toSpliced(questionIndex, 1, value));
            }}
            nextQuestion={() =>
              updateSelectedQuestion(currentQuestionIndex + 1, 'auto-advance')
            }
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexBasis: { xs: 'auto', md: '80px' },
            px: { xs: 1, md: 4 },
            py: { xs: 0.5, md: 2 },
            boxShadow: ({ palette }) => `0px -2px 2px ${palette.grey50}`,
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1,
          }}>
          <Box display="flex" gap={1}>
            <IconButton
              disabled={currentQuestionIndex === 0}
              aria-label="Previous"
              sx={{ backgroundColor: 'grey50', p: 1 }}
              onClick={() =>
                updateSelectedQuestion(currentQuestionIndex - 1, 'prev_button')
              }>
              <ChevronLeftRounded />
            </IconButton>
            <IconButton
              disabled={
                currentQuestionIndex + 1 === assessment?.questions.length
              }
              aria-label="Next"
              sx={{ backgroundColor: 'grey50', p: 1 }}
              onClick={() => updateSelectedQuestion(currentQuestionIndex + 1)}>
              <ChevronRightRounded />
            </IconButton>
          </Box>
          <Box>
            <Button
              variant={answers.includes(null) ? 'tertiary' : 'gradientPrimary'}
              label="Submit Assessment"
              onClick={() => setConfirmSubmit(true)}
              disabled={answers.includes(null)}
              skinny={false}
            />
          </Box>
        </Box>
      </Box>
      {confirmSubmit && (
        <ConfirmModal
          title="Ready to submit?"
          description="Make sure you're happy with all of your answers before finalizing. Once you submit, you won't be able to make changes to this attempt."
          onConfirm={() => {
            onSubmitAssessment();
            setConfirmSubmit(false);
          }}
          onCancel={() => setConfirmSubmit(false)}
          confirmText="Yes, submit"
          cancelText="No, go back"
        />
      )}
    </Box>
  );
};
